/**
 * src/lib/mockData.js — Demo data in raw snake_case shape (matches Supabase rows).
 * See docs/SHAPE_CONTRACT.md.
 *
 * NOTE: The fetch* functions in src/lib/api/* no longer fall back to MOCK on error —
 * they return [] so the UI is empty but consistent. MOCK remains exported for any
 * legacy callers (Wave 2 will eventually delete this file).
 */

const today = new Date().toISOString().split('T')[0];
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x.toISOString().split('T')[0]; };

const PEOPLE = [
  { id: 'recPER001', display_name: 'Chris',  email: 'chris@acnautitech.com',  role: 'CEO / PM',         rate_eur_hr: 65, weekly_hours_target: 40 },
  { id: 'recPER002', display_name: 'Nico',   email: 'nico@acnautitech.com',   role: 'Designer / PM',    rate_eur_hr: 45, weekly_hours_target: 40 },
  { id: 'recPER003', display_name: 'Joana',  email: 'joana@acnautitech.com',  role: 'Confección',       rate_eur_hr: 30, weekly_hours_target: 40 },
  { id: 'recPER004', display_name: 'Zamir',  email: 'zamir@acnautitech.com',  role: 'Tapicero',         rate_eur_hr: 32, weekly_hours_target: 40 },
  { id: 'recPER005', display_name: 'Maria',  email: 'maria@acnautitech.com',  role: 'Admin',            rate_eur_hr: 28, weekly_hours_target: 40 },
];

const STAGES = [
  { id: 'recSTG001', stage_name: 'Inquiry',       slug: 'inquiry',       label_en: 'Inquiry',       label_es: 'Consulta',      sort_order: 1, color: 'var(--tm)' },
  // HEX_TODO[#3B82F6] exception — see color-map.json
  { id: 'recSTG002', stage_name: 'Survey / Scan', slug: 'survey',        label_en: 'Survey / Scan', label_es: 'Inspección',    sort_order: 2, color: '#3B82F6' },
  // HEX_TODO[#8B5CF6] exception — see color-map.json
  { id: 'recSTG003', stage_name: 'Design',        slug: 'design',        label_en: 'Design',        label_es: 'Diseño',        sort_order: 3, color: '#8B5CF6' },
  { id: 'recSTG004', stage_name: 'Approval',      slug: 'approval',      label_en: 'Approval',      label_es: 'Aprobación',    sort_order: 4, color: 'var(--warn)' },
  { id: 'recSTG005', stage_name: 'Materials',     slug: 'materials',     label_en: 'Materials',     label_es: 'Materiales',    sort_order: 5, color: 'var(--ok)' },
  // HEX_TODO[#06B6D4] exception — see color-map.json
  { id: 'recSTG006', stage_name: 'Production',    slug: 'production',    label_en: 'Production',    label_es: 'Producción',    sort_order: 6, color: '#06B6D4' },
  { id: 'recSTG007', stage_name: 'Installation',  slug: 'installation',  label_en: 'Installation',  label_es: 'Instalación',   sort_order: 7, color: 'var(--info)' },
  { id: 'recSTG008', stage_name: 'Complete',      slug: 'complete',      label_en: 'Complete',      label_es: 'Completado',    sort_order: 8, color: 'var(--ok)', is_terminal: true },
];

const VESSELS = [
  { id: 'recVES001', vessel_name: 'M/Y JungleFever',  make_model: 'Hanse 630',         year: 2019, loa_m: 19.3, status: 'active', is_superyacht: false },
  { id: 'recVES002', vessel_name: 'S/Y Buganvilia',   make_model: 'Beneteau 49',       year: 2021, loa_m: 14.6, status: 'active', is_superyacht: false },
  { id: 'recVES003', vessel_name: 'M/Y CLAROR',       make_model: 'Cranchi Smeraldo',  year: 2022, loa_m: 13.8, status: 'active', is_superyacht: false },
  { id: 'recVES004', vessel_name: 'S/Y Windborne',    make_model: 'Jeanneau 54',       year: 2018, loa_m: 16.2, status: 'active', is_superyacht: false },
  { id: 'recVES005', vessel_name: 'M/Y Margaret Ann', make_model: 'Custom 42m',        year: 2015, loa_m: 42.0, status: 'active', is_superyacht: true },
];

const PROJECTS = [
  {
    id: 'recPRJ001', project_name: 'JungleFever Sprayhood + Bimini', project_number: 17,
    short_description: 'Full sprayhood replacement plus new bimini with integrated LED lighting',
    owner_id: 'recPER002', vessel_id: 'recVES001', stage_id: 'recSTG003', project_type_id: null,
    stages: STAGES.find(s => s.id === 'recSTG003'),
    user_profiles: { display_name: 'Nico' },
    vessels: VESSELS.find(v => v.id === 'recVES001'),
  },
  {
    id: 'recPRJ002', project_name: 'Buganvilia Interior Upholstery', project_number: 18,
    short_description: 'Complete salon cushion reupholstery in marine leather',
    owner_id: 'recPER001', vessel_id: 'recVES002', stage_id: 'recSTG005', project_type_id: null,
    stages: STAGES.find(s => s.id === 'recSTG005'),
    user_profiles: { display_name: 'Chris' },
    vessels: VESSELS.find(v => v.id === 'recVES002'),
  },
  {
    id: 'recPRJ003', project_name: 'CLAROR Full Cover', project_number: 19,
    short_description: 'Custom winter storage cover for Cranchi Smeraldo',
    owner_id: 'recPER003', vessel_id: 'recVES003', stage_id: 'recSTG006', project_type_id: null,
    stages: STAGES.find(s => s.id === 'recSTG006'),
    user_profiles: { display_name: 'Joana' },
    vessels: VESSELS.find(v => v.id === 'recVES003'),
  },
  {
    id: 'recPRJ004', project_name: 'Windborne Bimini', project_number: 20,
    short_description: 'Bimini frame repair and new canvas with side panels',
    owner_id: 'recPER002', vessel_id: 'recVES004', stage_id: 'recSTG002', project_type_id: null,
    stages: STAGES.find(s => s.id === 'recSTG002'),
    user_profiles: { display_name: 'Nico' },
    vessels: VESSELS.find(v => v.id === 'recVES004'),
  },
  {
    id: 'recPRJ005', project_name: 'Margaret Ann Bed Lining', project_number: 21,
    short_description: 'Superyacht master cabin bed lining and headboard',
    owner_id: 'recPER001', vessel_id: 'recVES005', stage_id: 'recSTG001', project_type_id: null,
    stages: STAGES.find(s => s.id === 'recSTG001'),
    user_profiles: { display_name: 'Chris' },
    vessels: VESSELS.find(v => v.id === 'recVES005'),
  },
];

const mkTask = (id, name, status, dept, days, assignee, projectId, hours = 4) => ({
  id, task_name: name, status, department: dept, priority: 'P2',
  start_date: addDays(today, days[0]), due_date: addDays(today, days[1]),
  assignee_id: assignee, project_id: projectId, bundle_id: null, stage_id: null,
  estimated_hours: hours, actual_hours: 0, notes: '', task_order: 0, archived_at: null,
  user_profiles: { display_name: PEOPLE.find(p => p.id === assignee)?.display_name || '' },
  projects: { project_name: PROJECTS.find(p => p.id === projectId)?.project_name || '', vessel_id: PROJECTS.find(p => p.id === projectId)?.vessel_id || null },
  stages: null,
});

const TASKS = [
  mkTask('recTSK001', 'Measure sprayhood frame dimensions', 'done',        'production',  [-14, -7], 'recPER002', 'recPRJ001', 6),
  mkTask('recTSK002', 'Design sprayhood pattern',           'in_progress', 'production',  [-5,   3], 'recPER002', 'recPRJ001', 8),
  mkTask('recTSK003', 'Order Sunbrella fabric',             'blocked',     'procurement', [-3,  -1], 'recPER005', 'recPRJ001', 2),
  mkTask('recTSK004', 'Bimini LED wiring layout',           'todo',        'installation',[ 5,  12], 'recPER004', 'recPRJ001', 6),
  mkTask('recTSK005', 'Select leather swatches for salon',  'done',        'procurement', [-20,-12], 'recPER002', 'recPRJ002', 4),
  mkTask('recTSK006', 'Cut salon cushion templates',        'in_progress', 'production',  [-4,   5], 'recPER003', 'recPRJ002', 8),
  mkTask('recTSK007', 'Sew cushion covers — port side',     'todo',        'production',  [ 6,  14], 'recPER003', 'recPRJ002', 12),
  mkTask('recTSK008', 'Install cushions on vessel',         'todo',        'installation',[15,  18], 'recPER004', 'recPRJ002', 6),
  mkTask('recTSK009', 'Sew main cover panels',              'in_progress', 'production',  [-6,   2], 'recPER003', 'recPRJ003', 10),
  mkTask('recTSK010', 'Install grommets and tie-downs',     'todo',        'production',  [ 3,   6], 'recPER004', 'recPRJ003', 4),
  mkTask('recTSK011', 'Deliver and fit cover on vessel',    'todo',        'installation',[ 7,   9], 'recPER004', 'recPRJ003', 4),
  mkTask('recTSK012', 'Survey bimini frame condition',      'in_progress', 'production',  [-2,   1], 'recPER002', 'recPRJ004', 3),
  mkTask('recTSK013', 'Quote frame repair welding',         'todo',        'procurement', [ 2,   5], 'recPER005', 'recPRJ004', 2),
];

const mkLog = (id, person, project, task, dayOffset, hours, work, notes) => ({
  id, person_id: person, project_id: project, task_id: task,
  date: addDays(today, dayOffset), duration_hours: hours, work_type: work, notes,
});

const TIME_LOG = [
  mkLog('recTL001', 'recPER002', 'recPRJ001', 'recTSK001', -10, 3,   'survey',     'On-board measurements'),
  mkLog('recTL002', 'recPER002', 'recPRJ001', 'recTSK002', -3,  5,   'design',     'Pattern drafting'),
  mkLog('recTL003', 'recPER002', 'recPRJ001', 'recTSK002', -1,  4,   'design',     'Pattern revision'),
  mkLog('recTL004', 'recPER003', 'recPRJ002', 'recTSK006', -2,  6,   'production', 'Template cutting'),
  mkLog('recTL005', 'recPER003', 'recPRJ003', 'recTSK009', -4,  7,   'production', 'Main panel sewing'),
  mkLog('recTL006', 'recPER003', 'recPRJ003', 'recTSK009', -3,  6.5, 'production', 'Panel sewing continued'),
  mkLog('recTL007', 'recPER004', 'recPRJ001', 'recTSK001', -8,  2,   'survey',     'Helped with measurements'),
  mkLog('recTL008', 'recPER002', 'recPRJ004', 'recTSK012',  0,  3.5, 'survey',     'Frame inspection at marina'),
  mkLog('recTL009', 'recPER005', 'recPRJ001', 'recTSK003', -1,  1,   'admin',      'Supplier follow-up call'),
  mkLog('recTL010', 'recPER001', 'recPRJ005', null,        -1,  1.5, 'sales',      'Initial call with captain'),
];

const CRM = [
  { id: 'recCRM001', opportunity: 'Margaret Ann — Bed Lining', status: 'Lead',          project_value: 18500, owner_id: 'recPER001', vessel_id: 'recVES005', notes: 'Superyacht, captain in Tarragona', user_profiles: { display_name: 'Chris' }, vessels: VESSELS[4] },
  { id: 'recCRM002', opportunity: 'FOSTER — Bimini',           status: 'Contacted',     project_value: 3200,  owner_id: 'recPER001', vessel_id: null,        notes: 'Oceanis 35.1, simple bimini', user_profiles: { display_name: 'Chris' }, vessels: null },
  { id: 'recCRM003', opportunity: 'CLAROR — Winter Cover',     status: 'Won',           project_value: 4800,  owner_id: 'recPER001', vessel_id: 'recVES003', notes: '', user_profiles: { display_name: 'Chris' }, vessels: VESSELS[2] },
  { id: 'recCRM004', opportunity: 'Delmiro — Bimini Bar',      status: 'Lead',          project_value: 2800,  owner_id: 'recPER001', vessel_id: null,        notes: 'Beneteau 41', user_profiles: { display_name: 'Chris' }, vessels: null },
  { id: 'recCRM005', opportunity: 'Pendennis — Colchones',     status: 'Lead',          project_value: 42000, owner_id: 'recPER001', vessel_id: 'recVES005', notes: 'Superyacht mattresses', user_profiles: { display_name: 'Chris' }, vessels: VESSELS[4] },
  { id: 'recCRM006', opportunity: 'S/Y Eclipse — Full Refit',  status: 'Proposal Sent', project_value: 28000, owner_id: 'recPER002', vessel_id: null,        notes: 'Quote pending', user_profiles: { display_name: 'Nico' }, vessels: null },
];

export const MOCK = {
  projects: PROJECTS,
  tasks: TASKS,
  people: PEOPLE,
  crm: CRM,
  timeLog: TIME_LOG,
  stages: STAGES,
  vessels: VESSELS,
};

// ─── Helpers (raw-row shape) ────────────────────────────
export function resolvePersonName(personId, allPeople) {
  if (!personId || !allPeople?.length) return 'Unassigned';
  const id = Array.isArray(personId) ? personId[0] : personId;
  const person = allPeople.find(p => p.id === id);
  return person?.display_name || 'Unknown';
}

export function computeProjectCompletion(projectId, allTasks) {
  const tasks = allTasks.filter(t => t.project_id === projectId);
  if (!tasks.length) return { total: 0, done: 0, pct: 0 };
  const done = tasks.filter(t => t.status === 'done').length;
  return { total: tasks.length, done, pct: Math.round((done / tasks.length) * 100) };
}

export function daysUntilDue(dueDate) {
  if (!dueDate) return null;
  return Math.ceil((new Date(dueDate) - new Date()) / (1000 * 60 * 60 * 24));
}

export function avatarColor(name) {
  if (!name) return 'var(--tm)';
  // HEX_TODO[#7C3AED] exception — see color-map.json
  // HEX_TODO[#1C3F75] possible: var(--accent)|var(--navy)
  const colors = ['#1C3F75', 'var(--accent)', 'var(--ok)', 'var(--warn)', 'var(--bad)', '#7C3AED', 'var(--info)'];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
}
